import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, AlertTriangle, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { API_BASE } from '@/utils/api';
import { money } from '@/components/tables/tableStates';
import { CashTabs } from './ModernCashier';
import { methodLabel } from '../shared';

const BILLS = [20000, 10000, 2000, 1000, 500, 200, 100];
const METHODS = ['cash', 'debit', 'credit', 'card', 'transfer'];

const Result = ({ diff }) => {
  const ok = Math.abs(diff) < 1;
  const label = ok ? 'Cuadra' : diff < 0 ? 'Falta' : 'Sobra';
  return (
    <div className={`rounded-xl p-4 text-center ${ok ? 'bg-[#dcfce7] text-[#14532d]' : 'bg-[#fee2e2] text-[#7f1d1d]'}`} data-testid="cash-result">
      <div className="flex items-center justify-center gap-2 text-2xl font-bold">{ok ? <CheckCircle2 className="w-7 h-7" /> : <AlertTriangle className="w-7 h-7" />}{label}</div>
      <p className="text-sm mt-1">{ok ? 'El conteo coincide con lo esperado.' : `Diferencia ${money(Math.abs(diff))}`}</p>
    </div>
  );
};

export default function ModernCashRegister() {
  const [regs, setRegs] = useState([]);
  const [rid, setRid] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [tab, setTab] = useState('close');
  const [count, setCount] = useState({});
  const [coins, setCoins] = useState('');
  const [openAmount, setOpenAmount] = useState('');
  const [pin, setPin] = useState('');
  const [notes, setNotes] = useState('');
  const load = useCallback(async () => {
    const { data } = await axios.get(`${API_BASE}/cash-registers`);
    setRegs(data);
    setRid(r => r || (data.find(x => x.status === 'open') || data[0])?.register_id || null);
  }, []);
  useEffect(() => { load(); }, [load]);
  const reg = regs.find(r => r.register_id === rid);
  useEffect(() => {
    if (!reg || reg.status !== 'open') return setSessions([]);
    axios.get(`${API_BASE}/cash-registers/${reg.register_id}/sessions`).then(r => setSessions(r.data.sessions || r.data || [])).catch(() => setSessions([]));
  }, [reg?.register_id, reg?.status]); // eslint-disable-line react-hooks/exhaustive-deps
  const byMethod = useMemo(() => METHODS.map(m => [m, sessions.filter(s => s.payment_method === m).reduce((a, s) => a + (s.total || 0), 0)]).filter(([, v]) => v), [sessions]);
  const byUser = useMemo(() => Object.values(sessions.reduce((acc, s) => {
    const k = s.closed_by_name || s.cashier_name || s.waiter_name || 'Sin usuario';
    const row = acc[k] || (acc[k] = { name: k, total: 0, n: 0, m: {} });
    row.total += s.total || 0; row.n += 1; row.m[s.payment_method] = (row.m[s.payment_method] || 0) + (s.total || 0);
    return acc;
  }, {})), [sessions]);
  const counted = BILLS.reduce((a, b) => a + b * (Number(count[b]) || 0), 0) + (Number(coins) || 0);
  const cashSales = sessions.filter(s => s.payment_method === 'cash').reduce((a, s) => a + (s.total || 0), 0);
  const expected = reg?.expected_amount ?? ((reg?.initial_amount || 0) + cashSales);
  const act = async (kind) => {
    try {
      const body = kind === 'open' ? { initial_amount: Number(openAmount) || 0, pin: pin || undefined } : { counted_amount: counted, notes, pin: pin || undefined };
      await axios.post(`${API_BASE}/cash-registers/${reg.register_id}/${kind}`, body);
      toast.success(kind === 'open' ? 'Caja abierta' : 'Caja cerrada');
      setPin(''); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo completar'); }
  };
  if (!reg) return <div className="p-6 space-y-4"><CashTabs active="/cash-register" /><div className="m-card p-5 max-w-md space-y-3"><p>No hay cajas configuradas.</p><Button className="bg-[#2563EB] text-white" data-testid="cash-create-btn" onClick={async () => { try { await axios.post(`${API_BASE}/cash-registers`, { name: 'Caja Principal', initial_amount: 0 }); load(); } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo crear'); } }}>Crear caja</Button></div></div>;
  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="modern-cash-register">
      <CashTabs active="/cash-register" />
      <div className="flex flex-wrap items-center gap-3">
        {regs.map(r => (
          <button key={r.register_id} onClick={() => setRid(r.register_id)} data-testid={`register-${r.register_id}`}
            className={`h-9 px-4 rounded-lg text-sm font-semibold ${rid === r.register_id ? 'bg-[#111827] text-white' : 'bg-muted'}`}>
            <span className={`inline-block w-2 h-2 rounded-full mr-2 ${r.status === 'open' ? 'bg-[#15803d]' : 'bg-[#6b7280]'}`} />{r.name} · {r.status === 'open' ? 'Abierta' : 'Cerrada'}
          </button>
        ))}
      </div>
      {reg.status !== 'open' ? (
        <section className="m-card p-5 max-w-md space-y-3" data-testid="cash-open">
          <h2 className="font-bold text-lg">Apertura de caja</h2>
          <Input type="number" placeholder="Monto inicial" value={openAmount} onChange={e => setOpenAmount(e.target.value)} data-testid="cash-open-amount" />
          {reg.pin_required && <Input type="password" placeholder="PIN" value={pin} onChange={e => setPin(e.target.value)} data-testid="cash-pin" />}
          <Button className="w-full h-11 bg-[#2563EB] text-white" onClick={() => act('open')} data-testid="cash-open-btn">Abrir caja</Button>
        </section>
      ) : (
        <>
          <div className="flex gap-5 border-b border-border" role="tablist">
            {[['summary', 'Movimientos'], ['close', 'Cierre de caja']].map(([k, l]) => (
              <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)} className="m-tab pb-2 text-sm font-medium text-muted-foreground" data-testid={`register-tab-${k}`}>{l}</button>
            ))}
          </div>
          <div className="grid lg:grid-cols-3 gap-4 items-start">
            <section className="m-card p-4 space-y-1.5 text-sm" data-testid="cash-summary">
              <h3 className="font-bold mb-2">Resumen del turno</h3>
              <div className="flex justify-between"><span>Monto inicial</span><b>{money(reg.initial_amount)}</b></div>
              <div className="flex justify-between"><span>Ventas totales</span><b>{money(sessions.reduce((a, s) => a + (s.total || 0), 0))}</b></div>
              {byMethod.map(([m, v]) => <div key={m} className="flex justify-between pl-3"><span>{methodLabel(m)}</span><span>{money(v)}</span></div>)}
              <div className="flex justify-between pt-1 border-t border-border"><span>Efectivo esperado</span><b>{money(expected)}</b></div>
            </section>
            {tab === 'close' ? (
              <section className="m-card p-4" data-testid="cash-count">
                <h3 className="font-bold mb-2">Conteo de efectivo</h3>
                <table className="w-full text-sm"><thead><tr className="text-left text-muted-foreground"><th>Billete</th><th>Cantidad</th><th className="text-right">Subtotal</th></tr></thead>
                  <tbody>{BILLS.map(b => (
                    <tr key={b}><td className="py-1">{money(b)}</td>
                      <td><Input type="number" min={0} className="h-8 w-20" value={count[b] || ''} onChange={e => setCount({ ...count, [b]: e.target.value })} data-testid={`bill-${b}`} /></td>
                      <td className="text-right tabular-nums">{money(b * (Number(count[b]) || 0))}</td></tr>
                  ))}
                    <tr><td className="py-1">Monedas</td><td><Input type="number" min={0} className="h-8 w-20" value={coins} onChange={e => setCoins(e.target.value)} data-testid="bill-coins" /></td><td className="text-right">{money(coins)}</td></tr>
                  </tbody></table>
                <p className="text-right font-bold mt-2">Total contado {money(counted)}</p>
              </section>
            ) : (
              <section className="m-card p-4 text-sm" data-testid="cash-movements">
                <h3 className="font-bold mb-2">Cobros del turno</h3>
                {sessions.slice(0, 15).map(s => <div key={s.session_id} className="flex justify-between py-1 border-b border-border"><span>{s.unit_name || 'Mesa'} · {methodLabel(s.payment_method)}</span><b>{money(s.total)}</b></div>)}
              </section>
            )}
            <section className="m-card p-4 space-y-3">
              <h3 className="font-bold">Resultado</h3>
              <Result diff={counted - expected} />
              <div className="text-sm flex justify-between"><span>Esperado en efectivo</span><b>{money(expected)}</b></div>
              <div className="text-sm flex justify-between"><span>Contado</span><b>{money(counted)}</b></div>
              <Input placeholder="Notas (opcional)" value={notes} onChange={e => setNotes(e.target.value)} data-testid="cash-close-notes" />
              {reg.pin_required && <Input type="password" placeholder="PIN" value={pin} onChange={e => setPin(e.target.value)} data-testid="cash-pin" />}
              <Button className="w-full h-11 bg-[#2563EB] text-white" onClick={() => act('close')} data-testid="cash-close-btn"><Lock className="w-4 h-4 mr-1" />Cerrar caja</Button>
            </section>
          </div>
          <section className="m-card p-4 overflow-x-auto" data-testid="cash-by-user">
            <h3 className="font-bold mb-2">Cobros por usuario</h3>
            <table className="w-full text-sm"><thead><tr className="text-left text-muted-foreground"><th>Usuario</th><th>Ventas</th>{['cash', 'debit', 'credit'].map(m => <th key={m}>{methodLabel(m)}</th>)}<th>Mesas</th></tr></thead>
              <tbody>{byUser.map(u => (
                <tr key={u.name} className="border-t border-border"><td className="py-1.5">{u.name}</td><td>{money(u.total)}</td>{['cash', 'debit', 'credit'].map(m => <td key={m}>{money(u.m[m])}</td>)}<td>{u.n}</td></tr>
              ))}</tbody></table>
          </section>
        </>
      )}
    </div>
  );
}
